import React from "react";
import {
	SEGMENT_BZA,
	URL_BZA_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
	URL_BZA_AIRCRAFT_DATA,
	URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
	URL_BZA_OPERATOR_CONTACT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Faq } from "../sections/faq/Faq";
import { Hero } from "../sections/hero/Hero";
import { Pricing } from "../sections/pricing/Pricing";
import { ProductTabs } from "../sections/ProductTabs";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { Trial } from "../sections/Trial";
import { Upgrades } from "../sections/Upgrades";
import { ProductSlider } from "../sections/ProductSlider";
import { productSegmentPageProps } from "../../common/types/product";

export default function BusinessOperatorAirportData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="52,000+ business jet operators and 14,000+ airports, with real-time updates and extensive details."
				packages={[
					{
						label: SEGMENT_BZA,
						features: [
							"Global coverage: Access 52,000+ business jet operators and 14,000 airport profiles",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title="Operator Data"
				tabs={[
					{
						title: "Operator profile",
						description:
							"Gain a comprehensive understanding of every business jet operator worldwide with detailed, up-to-date insights:",
						list: [
							"Full IATA & ICAO code database",
							"Status info (is the operator active, restarting, out of business, etc.)",
							"Base insights (at which airport, country, etc. are they based)",
							"Founding year",
							"Contact information (phone number, email address, website, and links to their social media accounts)",
							"Legal name and address",
							"Operator’s history (overview of rebrandings, merges, etc.)",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/operator-profile-overview.jpg",
						},
					},
					{
						title: "Fleets/Bases",
						description:
							"Get an overview of the fleet summary for each operator:",
						list: [
							"Fleet status (how many active, inactive, etc. aircraft do they have)",
							"Engine details (which engine types do they have in their fleet)",
							"Fleet history (how their fleet changed over time, etc.)",
							"Bases (list of all airports with operator’s bases)",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/operator-profile-fleets-bases.jpg",
						},
					},
					{
						title: "News",
						description:
							"Read news articles related to the specific business jet operator:",
						list: [
							"Latest news (all news in chronological order)",
							"General news (aircraft movements, operator business models, investments, FBO investments, aeropolitics and more)",
							"Fleet news (aircraft orders from manufacturers, eVTOL orders, second-hand aircraft purchases, tender/RFP, and more)",
							"Management changes (new appointments, resignations, etc.)",
							"Management changes (new appointments, resignations, etc.) ",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/operator-profile-news.jpg",
						},
					},
					{
						title: "Ownership/Partners",
						description:
							"Explore an operator's parent company relationships and subsidiaries:",
						list: [
							"Parent (operator’s parent info)",
							"Subsidiaries (list of subsidiary operators)",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/operator-profile-ownership.jpg",
						},
					},
					{
						title: "Contacts",
						description:
							"Get a detailed contacts overview for almost any business jet operator worldwide:",
						list: [
							"Name & job titles",
							"Emails and LinkedIn profiles",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/operator-profile-contacts.jpg",
						},
					},
					{
						title: "Accidents and Incidents",
						description:
							"Get an overview of accidents and incidents by business jet operator and/or aircraft variant:",
						list: [
							"Number of accidents and incidents by flight phase, flight nature, damage, occurrence, loss category so as the number of occupants & casualties",
							"List of accident and incident events including dates, aircraft details, location, etc.",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/operator-profile-accidents-incidents.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for business jets worldwide using a range of filters, such as location, operator, aircraft type, engine type, seat configuration and more."
				slides={[
					{
						title: "Operator",
						description: "Search for business jet operator by:",
						list: [
							"Name or code",
							"Operator type",
							"Operator status",
							"Planned start-up",
							"Your favorite lists",
							"Operator aircraft certification (part 91 or part 135)",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-data/operator-search-by-operator.jpg",
							title: "List of all active Part 91 operators",
						},
					},
					{
						title: "Location",
						description:
							"Search for business jet operator by location:",
						list: [
							"Homebase/airport/crew location",
							"Region",
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-data/operator-search-by-location.jpg",
							title: "List of all business jet operators in Texas",
						},
					},
					{
						title: "Fleet",
						description:
							"Search for business jet operator by fleet info:",
						list: [
							"Current fleet or fleet order",
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft family/aircraft/aircraft type/production line/aircraft role/model/variant",
							"Number of aircraft in fleet",
							"Total seats in fleet",
							"Aircraft seat range",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-data/operator-search-by-aircraft.jpg",
							title: "List of all business jet operators with Falcon 2000 in their fleet",
						},
					},
					{
						title: "Engines",
						description: "Search for business jet operator by:",
						list: [
							"Engine manufacturer",
							"Engine type/subtype",
							"Number of engines",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-data/operator-search-by-engines.jpg",
							title: "List of all business jet operators operating aircraft powered by GE engines",
						},
					},
				]}
			/>
			<ProductTabs
				title="Airport Data"
				tabs={[
					{
						title: "Airport Profile",
						description:
							"Get in-depth insights into airports worldwide with comprehensive data tailored for aviation professionals:",
						list: [
							"Full IATA & ICAO code database",
							"Airports categorized by business types (civil airport, airport no longer in use, etc.)",
							"Airport’s country, metro group, subdivision, coordinates and local time",
							"Elevation Info",
							"Runway Info (data on the longest runway)",
							"List of Operator’s based and Carriers Operating at the Airport",
							"List of aircraft stored and terminated at the airport",
							"List of Aircraft Types operating at the airport",
							"Today’s departures from the airport",
							"List of Countries and Destinations served from the Airport",
							"Google Maps (Airport shown on Google Maps)",
							"Airports Nearby (list of airports nearby including the distance from the original airport)",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/airport-profile-overview.jpg",
						},
					},
					{
						title: "News",
						description:
							"Read news articles related to the specific airport:",
						list: [
							"Latest news (all news in chronological order)",
							"General news (aircraft movements, operator business models, investments, FBO investments, aeropolitics and more)",
							"Fleet news (aircraft orders from manufacturers, eVTOL orders, second-hand aircraft purchases, tender/RFP, and more)",
							"Management changes (new appointments, resignations, etc.)",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/airport-profile-news.jpg",
						},
					},
					{
						title: "Crew Bases",
						description:
							"List of business jet operators with crew base at this airport.",
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/airport-profile-crews-bases.jpg",
						},
					},
					{
						title: "Accidents and Incidents ",
						description:
							"Get an overview of accidents and incidents reported at each airport:",
						list: [
							"Number of accidents and incidents by flight phase, flight nature, damage, occurrence, loss category, so as the number of occupants & casualties – based on origin or destination airport and location",
							"List of accident and incident events, including dates, aircraft details, location, etc., based on origin or destination airport and location",
						],
						visual: {
							url: "/static/img/product/business-jet-operator-airport-data/airport-profile-accidents-incidents.jpg",
						},
					},
				]}
				inverted={true}
			/>
			<ProductSlider
				description="Search for an airport using various filters, including name, code, and airport location."
				slides={[
					{
						title: "Airport",
						description: "Search for an airport by:",
						list: ["Name or code"],
						visual: {
							url: "/static/img/product/business-jet-aircraft-data/airport-search-by-name.jpg",
							title: "Overview of Teterbero airport",
						},
					},
					{
						title: "Location",
						description: "Search for an airport by location:",
						list: [
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-data/airport-search-by-location.jpg",
							title: "List of all airports in Malaysia",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator Contacts Data",
						content:
							"Get a detailed contacts overview for almost any active business jet operator worldwide.",
						url: URL_BZA_OPERATOR_CONTACT_DATA,
					},
					{
						title: "Business Jet Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 29,000 business jets currently being tracked.",
						url: URL_BZA_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into business jet aircraft owners, managers and SPVs.",
						url: URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
					},
					{
						title: "Aircraft Accident and Incident Data",
						content:
							"Get a comprehensive overview of accidents and incidents by operator and/or aircraft variant.",
						url: URL_BZA_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
					},
				]}
			>
				<Trial
					description="Get full, unrestricted access to all of ch-aviation’s news
					and data for 14 days. Our no-commitment guarantee:"
					subscribeLink={subscribeLink}
				/>
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				downloadsDescription="Our Business Jet Operator & Airport Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
				list={[
					"Global coverage: Access 52,000+ business jet operators and 14,000+ airport profiles",
				]}
				subscribeLink={subscribeLink}
			>
				<Trial
					description="Get full, unrestricted access to all of ch-aviation’s news
					and data for 14 days. Our no-commitment guarantee:"
					subscribeLink={subscribeLink}
				/>
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download these data sets?",
						content:
							"The Business Jet Operator Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. However, the Airport Data does not include this feature but is available through our Data Feeds option. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, both Operator and Airport Data can be
								purchased as a data feed for seamless
								integration into your own system. ch-aviation
								also provides full support for the integration
								process. To learn more about data feeds, click
								here.{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "Does ch-aviation track FBOs?",
						content:
							"ch-aviation does not currently track or include FBOs in its coverage.",
					},
					{
						title: "How often is the data updated?",
						content:
							"The frequency of updates depends on the data set. Operator and Airport Data are updated on a daily, weekly, or monthly basis, ensuring you always have the latest information.",
					},
				]}
			/>
		</>
	);
}
